import { useState, useEffect } from "react";
import {
  COOKIE_ACCESS_ROLE,
  COOKIE_ACCESS_MAIL,
  COOKIE_ACCESS_BOARD,
  COOKIE_ACCESS_EXAM,
  COOKIE_ACCESS_MEDIUM,
  COOKIE_ACCESS_SESSION,
  COOKIE_ACCESS_SECTION,
  COOKIE_ACCESS_SUBSCRIPTION_STATUS,
  COOKIE_ACCESS_SUBSCRIBED_COURSES,
} from "../utils/storage-constants";
import { SingletonContext } from "../contexts";
import { useApi } from "../hooks/use-api";
import { apiStudentStudys } from "../utils/api-routes";

const SingletonProvider = ({ children }) => {
  const [user, setUser] = useState({});
  const { get } = useApi();
  const [role, setRole] = useState(localStorage.getItem(COOKIE_ACCESS_ROLE));
  const [mail, setMail] = useState(localStorage.getItem(COOKIE_ACCESS_MAIL));
  const [board, setBoard] = useState(localStorage.getItem(COOKIE_ACCESS_BOARD));
  const [examType, setExamType] = useState(localStorage.getItem(COOKIE_ACCESS_EXAM));
  const [medium, setMedium] = useState(localStorage.getItem(COOKIE_ACCESS_MEDIUM));
  const [sessionTime, setSessionTime] = useState(localStorage.getItem(COOKIE_ACCESS_SESSION));
  const [section, setSection] = useState(localStorage.getItem(COOKIE_ACCESS_SECTION));
  const [subscription, setSubscription] = useState(localStorage.getItem(COOKIE_ACCESS_SUBSCRIPTION_STATUS));

  // ✅ Global subscription limit modal state
  const [subscriptionLimitModal, setSubscriptionLimitModal] = useState(false);

  const updateSubscriptionLimitModal = (value) => {
    setSubscriptionLimitModal(value);
  };

  const [subscribedCourses, setSubscribedCourses] = useState(() => {
    try {
      return JSON.parse(
        localStorage.getItem(COOKIE_ACCESS_SUBSCRIBED_COURSES) || "[]",
      );
    } catch {
      return [];
    }
  });

  const getUserStudiesDetails = async () => {
    if (!mail) return;
    try {
      const res = await get(apiStudentStudys(mail));
      if (res) {
        setUser(res?.data);
        updateRole(res?.data?.data?.role);
        if (res?.data?.data?.focus) {
          updateExam(res?.data?.data?.focus);
        }
        if (res?.data?.data?.board) {
          updateBoard(res?.data?.data?.board);
        }
        if (res?.data?.data?.medium) {
          updateMedium(res?.data?.data?.medium);
        }
        if (res?.data?.data?.section !== undefined) {
          updateSection(res?.data?.data?.section);
        }
        updateSubscribedCourses(res?.data?.data?.subscribed_courses);
      } else {
        alert(res?.message);
      }
    } catch (e) {
      console.log("Error on getting user studiesdetails", e);
    }
  };

  const updateUserData = (user) => setUser(user);

  const updateRole = (role) => {
    if (role) localStorage.setItem(COOKIE_ACCESS_ROLE, role);
    else localStorage.removeItem(COOKIE_ACCESS_ROLE);
    setRole(role);
  };

  const updateMail = (mail) => {
    if (mail) localStorage.setItem(COOKIE_ACCESS_MAIL, mail);
    else localStorage.removeItem(COOKIE_ACCESS_MAIL);
    setMail(mail);
  };

  const updateBoard = (board) => {
    if (board) localStorage.setItem(COOKIE_ACCESS_BOARD, board);
    else localStorage.removeItem(COOKIE_ACCESS_BOARD);
    setBoard(board);
  };

  const updateExam = (examType) => {
    if (examType) localStorage.setItem(COOKIE_ACCESS_EXAM, examType);
    else localStorage.removeItem(COOKIE_ACCESS_EXAM);
    setExamType(examType);
  };

  const updateMedium = (medium) => {
    if (medium) localStorage.setItem(COOKIE_ACCESS_MEDIUM, medium);
    else localStorage.removeItem(COOKIE_ACCESS_MEDIUM);
    setMedium(medium);
  };

  const updateSession = (sessionTime) => {
    if (sessionTime) localStorage.setItem(COOKIE_ACCESS_SESSION, sessionTime);
    else localStorage.removeItem(COOKIE_ACCESS_SESSION);
    setSessionTime(sessionTime);
  };

  const updateSection = (section) => {
    if (section) localStorage.setItem(COOKIE_ACCESS_SECTION, section);
    else localStorage.removeItem(COOKIE_ACCESS_SECTION);
    setSection(section);
  };

  const updateSubscription = (subscription) => {
    if (subscription) localStorage.setItem(COOKIE_ACCESS_SUBSCRIPTION_STATUS, subscription);
    else localStorage.removeItem(COOKIE_ACCESS_SUBSCRIPTION_STATUS);
    setSubscription(subscription);
  };

  const updateSubscribedCourses = (courses = []) => {
    const subscriptionList = Array.isArray(courses) ? courses : [];
    localStorage.setItem(
      COOKIE_ACCESS_SUBSCRIBED_COURSES,
      JSON.stringify(subscriptionList),
    );

    setSubscribedCourses(subscriptionList);
  };

  useEffect(() => {
    if (!mail) return;
    getUserStudiesDetails();
  }, [mail]);

  const value = {
    user,
    role,
    mail,
    board,
    examType,
    medium,
    sessionTime,
    section,
    subscription,
    subscriptionLimitModal,           // ✅
    updateSubscriptionLimitModal,     // ✅
    updateMail,
    updateRole,
    updateBoard,
    updateExam,
    updateUserData,
    updateMedium,
    updateSession,
    updateSection,
    updateSubscription,
    updateSubscribedCourses,
    getUserDetails: getUserStudiesDetails,
  };

  return (
    <SingletonContext.Provider value={value}>
      {children}
    </SingletonContext.Provider>
  );
};

export default SingletonProvider;